import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Table, Badge } from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIcon from 'mdi-react/DragIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import Loading from '../../shared/components/custom/Loading';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import FormCard from '../../shared/components/custom/FormCard';
import TiersShowcase from './components/TiersShowcase';
import useConfig from '../../util/useConfig';

const s = partial('shared');
const p = partial('UserTiers');
const UserTiers = () => {
  const config = useConfig();
  const history = useHistory();
  const [userTiers, setUserTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState({
    modalVisible: false,
    deleteId: null,
    deleteType: '',
  });
  const getAllTiers = async (onEnd = () => {}) => {
    setLoading(true);
    try {
      let { data } = await Api.tiers.allTiers({ withDefault: true });
      const removeTiers = ['Default tier', 'Guest user'];
      data = data.filter((item) => !removeTiers.includes(item.name));
      const tiersWithOrder = data.map((item, idx) => ({
        ...item,
        order: idx + 1,
      }));
      setUserTiers(tiersWithOrder);
      setLoading(false);
    } catch (err) {
      toast.error(err?.data?.message);
      setLoading(false);
    }
    onEnd();
  };
  useEffect(() => {
    getAllTiers();
  }, []);
  const handleModal = (state, id, newsType) => {
    setModalData({ modalVisible: state, deleteId: id, deleteType: newsType });
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      const deleteTierId = modalData.deleteId;
      await Api.tiers.deleteTier(deleteTierId, config);
      getAllTiers(() => {
        handleModal(false, null, 'tier');
        toast.success(p('tierDeleted'));
      });
    } catch (err) {
      handleModal(false, null, 'tier');
      toast.error(err);
      setLoading(false);
    }
  };
  const RenderDraggableTier = ({ tier, index }) => {
    return (
      <Draggable
        key={`group-${tier.id}`}
        draggableId={`group-${tier.id}`}
        index={index}
        isDragDisabled={false}
      >
        {(provided, snapshot) => (
          <RenderTierRow tier={tier} provided={provided} snapshot={snapshot} />
        )}
      </Draggable>
    );
  };
  const RenderTierRow = ({ tier, provided, snapshot }) => {
    return (
      <tr
        ref={provided?.innerRef}
        {...provided?.draggableProps}
        {...provided?.dragHandleProps}
        className={snapshot?.isDragging ? 'rbdnd-dragging' : 'center-list'}
        key={`tier-${tier.id}`}
      >
        {snapshot ? (
          <td className="flex-Vcenter" data-label="#">
            <DragIcon color="#999999" />
          </td>
        ) : (
          <td data-label="#" />
        )}
        {snapshot ? (
          <td data-label={s('priority')}>{tier.order}</td>
        ) : (
          <td data-label={s('priority')} />
        )}
        <td className="table-stack__primary">
          <Link to={`/user_tiers/edit/${tier.id}`} className="flex-Vcenter">
            <img className="teamLogoTable" src={tier?.originalUrl} alt="logo" />
            {tier.name}
          </Link>
        </td>
        <td data-label={p('forSaleTitle')}>
          <Badge pill color={tier.forSale ? 'success' : 'secondary'}>
            {tier.forSale ? p('saleStatusActive') : p('saleStatusInactive')}
          </Badge>
        </td>
        <td className="table-stack__actions">
          <div className="icon-actions">
            <SquareIconButton
              icon={<EditOutlineIcon />}
              onClick={() => history.push(`/user_tiers/edit/${tier.id}`)}
              title={s('edit')}
              id={`edit-tier-${tier.id}`}
            />
            {tier.canDelete && (
              <SquareIconButton
                icon={<DeleteOutlineIcon />}
                onClick={() => handleModal(true, tier.id, 'tier')}
                title={s('delete')}
                tone="danger"
                id={`delete-tier-${tier.id}`}
              />
            )}
          </div>
        </td>
      </tr>
    );
  };
  const renderTierList = () => {
    return userTiers.map((tier, index) => {
      const isDraggable = tier.loginStage == null;
      return isDraggable ? (
        <RenderDraggableTier tier={tier} index={index} key={tier.id} />
      ) : (
        <RenderTierRow tier={tier} key={tier.id} />
      );
    });
  };

  const groupOnDragEnd = async ({ destination, source }) => {
    if (!destination) {
      return;
    }
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    setLoading(true);
    const draggedGroup = userTiers[source.index];
    const newTiers = [...userTiers];
    newTiers.splice(source.index, 1);
    newTiers.splice(destination.index, 0, draggedGroup);
    setUserTiers(newTiers);
    const orderedIds = newTiers.map((tier) => tier.id);
    try {
      await Api.tiers.updateTierOrder({ orderedIds }, config);
      getAllTiers();
    } catch (err) {
      setLoading(false);
      toast.error(p('updatingOrderFailed'));
    }
  };

  return (
    <Container>
      <Loading loading={loading} />
      <TiersShowcase />
      <Row>
        <Col>
          <FormCard
            title={p('tierList')}
            actions={
              <Link to="/user_tiers/create">
                <Button size="sm" color="primary" className="mb-0">
                  {s('create')}
                </Button>
              </Link>
            }
            bodyClassName="form-card__body--flush"
            className="mb-3"
          >
            <DragDropContext onDragEnd={groupOnDragEnd}>
              <Table responsive striped className="table--roomy table--stack">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{s('priority')}</th>
                    <th className="sortable">
                      <div className="flex alignItems-center">{s('name')}</div>
                    </th>
                    <th>{p('forSaleTitle')}</th>
                    <th> </th>
                  </tr>
                </thead>
                <Droppable droppableId="group-dnd">
                  {(provided) => {
                    return (
                      <>
                        <tbody
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {!loading && userTiers.length === 0 && (
                            <tr>
                              <td colSpan={5} className="text-muted">
                                {p('noTiersYet')}
                              </td>
                            </tr>
                          )}
                          {renderTierList()}
                          {provided.placeholder}
                        </tbody>
                      </>
                    );
                  }}
                </Droppable>
              </Table>
            </DragDropContext>
          </FormCard>
        </Col>
      </Row>
      <DeleteModal
        type={p('tier')}
        visible={modalData.modalVisible}
        handleModal={handleModal}
        modalAction={handleDelete}
      />
    </Container>
  );
};

export default UserTiers;
